import React, { useState } from 'react';
import { Crown, Check, Star, Palette, Building, Lightbulb, Minimize, Briefcase, Zap, CheckCircle2, X, Unlock } from 'lucide-react';
import { InvoiceTemplate, INVOICE_TEMPLATES } from '../../types';
import toast from 'react-hot-toast';

interface TemplateSelectorProps {
  selectedTemplate: InvoiceTemplate;
  onTemplateSelect: (template: InvoiceTemplate) => void;
  onPurchaseTemplate: (template: InvoiceTemplate) => void;
  userAccessibleTemplates?: string[];
  loadingAccess?: boolean;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplate,
  onTemplateSelect,
  onPurchaseTemplate,
  userAccessibleTemplates = [],
  loadingAccess = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFeatures, setShowFeatures] = useState<string | null>(null);

  const categories = [
    { id: 'all', name: 'All Templates', icon: Palette },
    { id: 'business', name: 'Business', icon: Building },
    { id: 'creative', name: 'Creative', icon: Lightbulb },
    { id: 'minimal', name: 'Minimal', icon: Minimize },
    { id: 'corporate', name: 'Corporate', icon: Briefcase },
    { id: 'modern', name: 'Modern', icon: Zap },
  ];

  const filteredTemplates = selectedCategory === 'all' 
    ? INVOICE_TEMPLATES 
    : INVOICE_TEMPLATES.filter(template => template.category === selectedCategory);

  const handleTemplateClick = (template: InvoiceTemplate) => {
    if (template.isPremium && !userAccessibleTemplates.includes(template.id)) {
      onPurchaseTemplate(template);
    } else {
      onTemplateSelect(template);
      toast.success(`Template "${template.name}" selected!`);
    }
  };

  const toggleFeatures = (templateId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Evita que o clique chegue ao card do template
    setShowFeatures(showFeatures === templateId ? null : templateId);
  };

  // Verifica se o template está desbloqueado
  const isTemplateUnlocked = (templateId: string) => {
    return userAccessibleTemplates.includes(templateId);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Template</h3>
        <p className="text-gray-600">Select from our collection of professional invoice templates</p>
      </div>

      {/* Loading indicator */}
      {loadingAccess && (
        <div className="mb-4 p-2 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700 flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Carregando templates disponíveis...
          </p>
        </div>
      )}

      {/* Category Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <category.icon className="h-4 w-4" />
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className={`relative group cursor-pointer rounded-xl border-2 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
              selectedTemplate.id === template.id
                ? 'border-blue-500 shadow-lg'
                : 'border-gray-200 hover:border-blue-300'
            }`}
            onClick={() => handleTemplateClick(template)}
          >
            {/* Premium Badge */}
            {template.isPremium && !isTemplateUnlocked(template.id) && (
              <div className="absolute top-3 right-3 z-10">
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                  <Crown className="h-3 w-3" />
                  <span>PRO</span>
                </div>
              </div>
            )}

            {/* Unlocked Badge */}
            {template.isPremium && isTemplateUnlocked(template.id) && (
              <div className="absolute top-3 right-3 z-10">
                <div className="bg-gradient-to-r from-green-400 to-green-600 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                  <Unlock className="h-3 w-3" />
                  <span>DESBLOQUEADO</span>
                </div>
              </div>
            )}

            {/* Selected Badge */}
            {selectedTemplate.id === template.id && (
              <div className="absolute top-3 left-3 z-10">
                <div className="bg-blue-600 text-white p-1 rounded-full">
                  <Check className="h-4 w-4" />
                </div>
              </div>
            )}

            {/* Template Preview */}
            <div 
              className="h-48 rounded-t-xl bg-gradient-to-br p-6 flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})`
              }}
            >
              <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 w-full max-w-[200px] shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  {template.logo && (
                    <img 
                      src={template.logo} 
                      alt="Logo" 
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  )}
                  <div className="text-right">
                    <div className="text-xs font-bold" style={{ color: template.colors.primary }}>
                      INVOICE
                    </div>
                    <div className="text-xs text-gray-600">#INV-001</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="h-2 bg-gray-200 rounded"></div>
                  <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
                
                <div className="mt-3 pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Total:</span>
                    <span className="text-sm font-bold" style={{ color: template.colors.primary }}>
                      $1,250.00
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Info */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{template.name}</h4>
                {template.isPremium && !isTemplateUnlocked(template.id) && (
                  <div className="flex items-center space-x-1 text-green-600">
                    <span className="text-sm font-bold">${template.price.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
              
              {/* Color Palette */}
              <div className="flex items-center space-x-2 mb-3">
                <span className="text-xs text-gray-500">Colors:</span>
                <div className="flex space-x-1">
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: template.colors.primary }}
                  ></div>
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: template.colors.secondary }}
                  ></div>
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: template.colors.accent }}
                  ></div>
                </div>
              </div>

              {/* Features Toggle Button */}
              {template.isPremium && template.features && (
                <div className="mb-3">
                  <button
                    onClick={(e) => toggleFeatures(template.id, e)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center"
                  >
                    {showFeatures === template.id ? 'Hide features' : 'Show premium features'}
                  </button>
                </div>
              )}

              {/* Features List (visível apenas quando expandido) */}
              {template.isPremium && showFeatures === template.id && (
                <div className="mb-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="text-xs font-medium text-gray-700 mb-2">Premium Features:</div>
                  <ul className="text-xs text-gray-600">
                    {template.features?.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-1 mb-1">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2">
                    <div className="flex items-center space-x-1">
                      {template.watermarkRemoval ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">No Watermark</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.customHeader ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Custom Header</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.customFooter ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Custom Footer</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.attachmentSupport ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Attachments</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.multiCurrency ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Multi-Currency</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.digitalSignature ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Digital Signature</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {template.itemImages ? 
                        <CheckCircle2 className="h-3 w-3 text-green-600" /> : 
                        <X className="h-3 w-3 text-gray-400" />
                      }
                      <span className="text-xs">Item Images</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <button
                className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  template.isPremium && !isTemplateUnlocked(template.id)
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:from-yellow-500 hover:to-orange-600'
                    : selectedTemplate.id === template.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {template.isPremium && !isTemplateUnlocked(template.id) ? (
                  <span className="flex items-center justify-center space-x-2">
                    <Crown className="h-4 w-4" />
                    <span>Subscribe for ${template.price.toFixed(2)}/month</span>
                  </span>
                ) : selectedTemplate.id === template.id ? (
                  'Selected'
                ) : (
                  'Select Template'
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Premium Benefits */}
      <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-2 rounded-lg">
            <Star className="h-5 w-5" />
          </div>
          <h4 className="text-lg font-bold text-gray-900">Premium Template Benefits</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Custom logo integration</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Unique color schemes</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Professional branding</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Enhanced layouts</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Lifetime access</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Commercial use</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">No watermark options</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Digital signatures</span>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm text-gray-700">Multi-currency support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;